import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Trophy, Medal, Home, RotateCcw, Share2, Crown } from 'lucide-react';
import { useGameStore, selectSortedPlayers } from '@/store';
import { formatScore } from '@party-game/shared';

export function ResultsPage() {
  const navigate = useNavigate();
  const { code } = useParams<{ code: string }>();
  const { player, room, players, reset } = useGameStore();

  const sortedPlayers = selectSortedPlayers(useGameStore.getState());

  // Redirect if no data
  useEffect(() => {
    if (!room || players.length === 0) {
      navigate('/');
    }
  }, [room, players, navigate]);

  const handlePlayAgain = () => {
    navigate(`/lobby/${code}`);
  };

  const handleHome = () => {
    reset();
    navigate('/');
  };

  const handleShare = async () => {
    const winner = sortedPlayers[0];
    const text = `🏆 انتهت لعبة الحفلة!\n\nالفائز: ${winner?.name} بـ ${winner?.score} نقطة\n\nالعب معنا:`;
    const url = `${window.location.origin}/join/${code}`;

    if (navigator.share) {
      await navigator.share({ title: 'نتائج لعبة الحفلة', text, url });
    } else {
      await navigator.clipboard.writeText(`${text} ${url}`);
    }
  };

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1: return '🥇';
      case 2: return '🥈';
      case 3: return '🥉';
      default: return `${rank}`;
    }
  };

  const getRankColor = (rank: number) => {
    switch (rank) {
      case 1: return 'from-yellow-400 to-yellow-600';
      case 2: return 'from-gray-300 to-gray-500';
      case 3: return 'from-amber-600 to-amber-800';
      default: return 'from-white/20 to-white/10';
    }
  };

  return (
    <div className="min-h-screen p-4 flex flex-col items-center justify-center">
      {/* Confetti effect */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        {Array.from({ length: 50 }).map((_, i) => (
          <div
            key={i}
            className="confetti"
            style={{
              left: `${Math.random() * 100}%`,
              backgroundColor: ['#e94560', '#ffd700', '#0ea5e9', '#10b981'][Math.floor(Math.random() * 4)],
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${2 + Math.random() * 2}s`,
            }}
          />
        ))}
      </div>

      <div className="max-w-2xl w-full relative z-10">
        {/* Trophy Icon */}
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: 'spring', bounce: 0.5 }}
          className="text-center mb-8"
        >
          <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-game-gold to-yellow-600 rounded-full shadow-2xl shadow-game-gold/40">
            <Trophy className="w-12 h-12 text-white" />
          </div>
        </motion.div>

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-4xl font-bold text-center mb-2"
        >
          انتهت اللعبة! 🎉
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="text-white/60 text-center mb-8"
        >
          {room?.gameType === 'spy' ? '🕵️ لعبة الجاسوس' : '❓ لعبة الأسئلة'}
        </motion.p>

        {/* Winner Card */}
        {sortedPlayers[0] && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 }}
            className="card mb-6 border-2 border-game-gold/30 bg-gradient-to-br from-game-gold/10 to-transparent"
          >
            <div className="text-center py-4">
              <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-game-gold to-yellow-600 rounded-full flex items-center justify-center text-4xl shadow-lg">
                {sortedPlayers[0].name.charAt(0)}
              </div>
              <div className="flex items-center justify-center gap-2 mb-1">
                <Crown className="w-6 h-6 text-game-gold" />
                <h2 className="text-2xl font-bold">{sortedPlayers[0].name}</h2>
              </div>
              <p className="text-game-gold text-3xl font-bold">
                {formatScore(sortedPlayers[0].score)} نقطة
              </p>
              {sortedPlayers[0].id === player?.id && (
                <span className="badge badge-gold mt-2">🎊 أنت الفائز!</span>
              )}
            </div>
          </motion.div>
        )}

        {/* All Players Ranking */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="card mb-6"
        >
          <h3 className="font-bold mb-4 flex items-center gap-2">
            <Medal className="w-5 h-5 text-primary-400" />
            الترتيب النهائي
          </h3>

          <div className="space-y-2">
            {sortedPlayers.map((p, index) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 + index * 0.1 }}
                className={`flex items-center gap-3 p-3 rounded-xl ${
                  p.id === player?.id
                    ? 'bg-primary-500/20 border border-primary-500/30'
                    : 'bg-white/5'
                }`}
              >
                {/* Rank */}
                <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${getRankColor(index + 1)} flex items-center justify-center font-bold`}>
                  {getRankIcon(index + 1)}
                </div>

                {/* Name */}
                <div className="flex-1">
                  <span className="font-bold">{p.name}</span>
                  {p.id === player?.id && (
                    <span className="text-xs text-white/40 mr-1">(أنت)</span>
                  )}
                  {p.role === 'host' && (
                    <Crown className="w-3 h-3 inline mr-1 text-game-gold" />
                  )}
                </div>

                {/* Score */}
                <div className="text-left">
                  <span className="text-game-gold font-bold text-lg">
                    {formatScore(p.score)}
                  </span>
                  <span className="text-white/40 text-sm mr-1">نقطة</span>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="grid grid-cols-3 gap-3"
        >
          <button
            onClick={handleHome}
            className="btn-outline flex flex-col items-center gap-2 py-4"
          >
            <Home className="w-6 h-6" />
            <span className="text-sm">الرئيسية</span>
          </button>

          <button
            onClick={handlePlayAgain}
            className="btn-primary flex flex-col items-center gap-2 py-4"
          >
            <RotateCcw className="w-6 h-6" />
            <span className="text-sm">العب مجدداً</span>
          </button>

          <button
            onClick={handleShare}
            className="btn-secondary flex flex-col items-center gap-2 py-4"
          >
            <Share2 className="w-6 h-6" />
            <span className="text-sm">شارك</span>
          </button>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="mt-8 grid grid-cols-3 gap-4 text-center"
        >
          <div className="card py-4">
            <p className="text-2xl font-bold text-primary-400">{players.length}</p>
            <p className="text-sm text-white/60">لاعب</p>
          </div>
          <div className="card py-4">
            <p className="text-2xl font-bold text-game-gold">
              {sortedPlayers.reduce((sum, p) => sum + p.score, 0)}
            </p>
            <p className="text-sm text-white/60">مجموع النقاط</p>
          </div>
          <div className="card py-4">
            <p className="text-2xl font-bold text-game-accent">
              {room?.gameType === 'spy' ? '🕵️' : '❓'}
            </p>
            <p className="text-sm text-white/60">نوع اللعبة</p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
